import React, { forwardRef, useEffect, useRef } from 'react'

interface ModalProps {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  title?: string
  className?: string
}

export const Modal = forwardRef<HTMLDialogElement, ModalProps>(({
  open,
  onClose,
  children,
  title,
  className = '',
}, ref) => {
  const internalRef = useRef<HTMLDialogElement>(null)
  const dialogRef = (ref as React.RefObject<HTMLDialogElement>) || internalRef

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (open) {
      dialog.showModal()
    } else {
      dialog.close()
    }
  }, [open, dialogRef])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const handleClose = () => onClose()
    dialog.addEventListener('close', handleClose)
    return () => dialog.removeEventListener('close', handleClose)
  }, [onClose, dialogRef])

  return (
    <dialog
      ref={dialogRef}
      className={`
        backdrop:bg-black/50
        bg-surface rounded-lg shadow-xl
        p-0 m-auto
        w-[calc(100vw-2rem)] max-w-lg
        md:w-full
        max-h-[calc(100vh-2rem)]
        overflow-y-auto
        ${className}
      `}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="p-6">
        {title && (
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground font-heading">{title}</h2>
            <button
              onClick={onClose}
              className="text-foreground-muted hover:text-foreground p-1 rounded transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        {children}
      </div>
    </dialog>
  )
})

Modal.displayName = 'Modal'
